/**
 * Universal listing search — the one behind the nav bar.
 *
 * Three ways in, and they compose:
 *
 *   1. every location          searchListings(all, { query: 'helicopter' })
 *   2. one location            searchListings(all, { location: 'Kariba' })
 *   3. a name within a location searchListings(all, { query: 'lodge', location: 'Kariba' })
 *
 * LOCATION IS MATCHED ON THE CANONICAL VALUE, by exact string equality, the
 * same way the destination page filters. `listings.location` stores the bare
 * canonical name and nothing normalises it at read time, so a "contains" match
 * here would quietly make 'Kariba' also return listings in a place merely
 * mentioning it. See data/locations.js for why the value is the only safe key.
 *
 * The free-text half is deliberately loose — a traveller types "heli", not the
 * listing's registered name. It matches across name, location, type, company
 * and tags, so "safari", "Kariba" and "Wild Horizons" all find something
 * without the traveller having to know which field they are searching.
 *
 * Description is searched LAST and only for the whole phrase, because it is
 * prose: nearly every listing's description mentions its own town, and
 * including it in the token match made a search for a place name return the
 * entire catalogue.
 */

/** Fields matched token by token. Order is the ranking order below. */
const TOKEN_FIELDS = ['name', 'location', 'type', 'company'];

const lower = (value) => (typeof value === 'string' ? value.toLowerCase() : '');

/** The haystack for one listing: its short fields plus its tags. */
function tokenHaystack(listing) {
  const fields = TOKEN_FIELDS.map((key) => lower(listing?.[key]));
  const tags = Array.isArray(listing?.tags) ? listing.tags.map(lower) : [];
  return [...fields, ...tags].join(' ');
}

/**
 * Every token must appear somewhere. "vic falls lodge" should not return
 * everything containing "lodge" — narrowing as you type is the whole point.
 */
function matchesQuery(listing, tokens, phrase) {
  const haystack = tokenHaystack(listing);
  if (tokens.every((token) => haystack.includes(token))) return true;
  // Whole-phrase fallback into the prose, for someone quoting a sentence they
  // read on the listing itself.
  return phrase.length >= 3 && lower(listing?.description).includes(phrase);
}

/**
 * Rank: a name match beats a location match beats anything else. Without this
 * the map opened on whatever order Firestore happened to return, which for a
 * search of a listing BY NAME is the one thing a traveller does not want.
 */
function score(listing, tokens, phrase) {
  const name = lower(listing?.name);
  if (!tokens.length) return 0;
  if (name === phrase) return 0;              // exact name
  if (name.startsWith(phrase)) return 1;
  if (tokens.every((t) => name.includes(t))) return 2;
  if (tokens.every((t) => lower(listing?.location).includes(t))) return 3;
  return 4;
}

/**
 * @param {Array} listings                 every listing in scope
 * @param {{query?: string, location?: string, type?: string, filter?: string}} criteria
 *        `location` is a canonical value and `type` a master category; both
 *        are matched by exact equality, and anything falsy means "no such
 *        constraint". `filter` is one of that category's sub-filters, matched
 *        against `listing.filters`.
 * @returns {Array} matches, best first, in stable order within a rank
 */
export function searchListings(listings, criteria = {}) {
  const source = Array.isArray(listings) ? listings : [];
  const phrase = (criteria.query ?? '').trim().toLowerCase();
  const tokens = phrase.split(/\s+/).filter(Boolean);
  const location = (criteria.location ?? '').trim();
  const type = (criteria.type ?? '').trim();
  const filter = (criteria.filter ?? '').trim();

  const filtered = source.filter((listing) => {
    if (!listing) return false;
    // Exact, not `includes` — see the note at the top of this file.
    if (location && listing.location !== location) return false;
    // Same reasoning: `type` and the sub-filters are picked from the platform
    // taxonomy, so a menu link means one category exactly. Matching them as
    // free text would make "Restaurants" also return every lodge whose
    // description mentions one.
    if (type && listing.type !== type) return false;
    if (filter && !(Array.isArray(listing.filters) && listing.filters.includes(filter))) return false;
    if (!tokens.length) return true;
    return matchesQuery(listing, tokens, phrase);
  });

  // `map`+`sort`+`map` rather than sorting in place: `listings` is the
  // context's snapshot array, and sorting it would reorder the home page too.
  return filtered
    .map((listing, index) => ({ listing, index, rank: score(listing, tokens, phrase) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.listing);
}

/**
 * A one-line description of what was searched for, for the results heading.
 * Says what the traveller asked for rather than echoing an empty string.
 */
export function describeSearch({ query, location, type, filter } = {}) {
  const q = (query ?? '').trim();
  const where = (location ?? '').trim();
  // The sub-filter is the more specific of the two, so it wins the headline:
  // "Luxury Safari" says more than "Safari Lodges & Camps" to someone who
  // just chose it from the menu.
  const what = (filter ?? '').trim() || (type ?? '').trim();

  const subject = q ? `“${q}”` : what;

  if (subject && where) return `${subject} in ${where}`;
  if (subject) return subject;
  if (where) return where;
  return 'Everywhere';
}
